export type UploadedResource = {
  id?: string
  url: string
  name?: string
  size?: number
  type?: string
  uploadedAt: number
}

export type UploadedResourceKind = "audio" | "file" | "image" | "video"

export const MAX_VIDEO_DURATION_SECONDS = 4 * 60
export const VIDEO_UPLOAD_TARGET_SIZE_MEGABYTES = 50
export const getVideoUploadRequirements = (
  targetSizeMegabytes = VIDEO_UPLOAD_TARGET_SIZE_MEGABYTES,
) =>
  `本地视频仅支持 MP4，原文件超过 ${targetSizeMegabytes}MB 将在上传前自动压缩`
export const VIDEO_UPLOAD_REQUIREMENTS = getVideoUploadRequirements()

export const getVideoDurationLimitError = (duration: number) => {
  return Number.isFinite(duration) && duration > MAX_VIDEO_DURATION_SECONDS
    ? "视频时长不能超过4分钟"
    : ""
}

export const imageUploadDimensionLimits = {
  maxAspectRatio: 2.5,
  maxHeight: 6000,
  maxWidth: 6000,
  minAspectRatio: 0.4,
  minHeight: 300,
  minWidth: 300,
} as const
export const uploadResourceLimits = {
  image: {
    accept: "image/png,image/jpg,image/jpeg",
    extensions: [".png", ".jpg", ".jpeg"],
    label: "图片",
    maxSize: 30 * 1024 * 1024,
    mimeTypes: ["image/png", "image/jpg", "image/jpeg"],
    sizeText: "30MB",
  },
  video: {
    accept: "video/mp4",
    extensions: [".mp4"],
    label: "视频",
    maxSize: 100 * 1024 * 1024,
    mimeTypes: ["video/mp4"],
    sizeText: "100MB",
  },
  audio: {
    accept: "audio/mpeg,audio/wav",
    extensions: [".mp3", ".mpeg", ".wav"],
    label: "音频",
    maxSize: 10 * 1024 * 1024,
    mimeTypes: ["audio/mpeg", "audio/wav"],
    sizeText: "10MB",
  },
} as const

export type UploadResourceLimitKind = keyof typeof uploadResourceLimits

export const IMAGE_UPLOAD_REQUIREMENTS = `图片 ${uploadResourceLimits.image.extensions.map((extension) => extension.slice(1).toUpperCase()).join("/")} 不超过 ${uploadResourceLimits.image.sizeText}，宽高 ${imageUploadDimensionLimits.minWidth}-${imageUploadDimensionLimits.maxWidth}px，最长边不超过最短边的 ${imageUploadDimensionLimits.maxAspectRatio} 倍`

export const maxVideoFileSize = uploadResourceLimits.video.maxSize

const getUploadedResourceText = (resource: UploadedResource) => {
  return [resource.type, resource.name, resource.id, resource.url]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
}

export const getUploadedResourceKind = (
  resource: UploadedResource,
): UploadedResourceKind => {
  const resourceName = resource.name || ""
  const resourceText = getUploadedResourceText(resource)
  const resourceType = resource.type?.toLowerCase() || ""

  // 这些名称是前端生成的 @ 引用标签，优先于后端可能缺失或不准确的 MIME。
  if (/^@?图片\d+$/i.test(resourceName)) return "image"
  if (/^@?视频\d+$/i.test(resourceName)) return "video"
  if (/^@?音频\d+$/i.test(resourceName)) return "audio"

  if (resourceType === "image" || resourceType.startsWith("image/")) {
    return "image"
  }
  if (resourceType === "video" || resourceType.startsWith("video/")) {
    return "video"
  }
  if (resourceType === "audio" || resourceType.startsWith("audio/")) {
    return "audio"
  }

  if (/\.(png|jpe?g|webp|gif|bmp|svg)(\?|$|\s)/i.test(resourceText)) {
    return "image"
  }
  if (/\.(mp4|mov|webm|m4v)(\?|$|\s)/i.test(resourceText)) {
    return "video"
  }
  if (/\.(mp3|wav|m4a|aac|ogg)(\?|$|\s)/i.test(resourceText)) {
    return "audio"
  }

  return "file"
}

export const getUploadedResourceMaterialLabel = (
  resources: UploadedResource[],
) => {
  const kinds = new Set(
    resources.map((resource) => getUploadedResourceKind(resource)),
  )

  if (kinds.size === 0) return "无"
  if (kinds.size === 1) {
    if (kinds.has("image")) return "图片"
    if (kinds.has("video")) return "视频"
    if (kinds.has("audio")) return "音频"
    return "文件"
  }

  const labels = [
    kinds.has("image") ? "图" : "",
    kinds.has("video") ? "视频" : "",
    kinds.has("audio") ? "音频" : "",
    kinds.has("file") ? "文件" : "",
  ].filter(Boolean)

  return labels.join("+")
}

export const isMp4Resource = (
  resource: Pick<UploadedResource, "name" | "type" | "url">,
) => {
  const resourceText = [resource.type, resource.name, resource.url]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()

  return resource.type === "video/mp4" || /\.mp4(\?|$|\s)/i.test(resourceText)
}

const getFileExtensionMatched = (filename: string, extensions: string[]) => {
  const normalizedFilename = filename.toLowerCase()

  return extensions.some((extension) => normalizedFilename.endsWith(extension))
}

export const isAllowedUploadFileType = (
  file: Pick<File, "name" | "type">,
  kind: UploadResourceLimitKind,
) => {
  const limit = uploadResourceLimits[kind]
  const fileType = file.type.toLowerCase()

  return (
    limit.mimeTypes.includes(fileType as never) ||
    getFileExtensionMatched(file.name, [...limit.extensions])
  )
}

export const getUploadFileMimeType = (
  file: Pick<File, "name" | "type">,
  kind: UploadResourceLimitKind,
) => {
  const limit = uploadResourceLimits[kind]
  const fileType = file.type.toLowerCase()

  if (limit.mimeTypes.includes(fileType as never)) {
    return file.type
  }

  const filename = file.name.toLowerCase()

  if (kind === "image") {
    if (filename.endsWith(".png")) return "image/png"
    if (filename.endsWith(".jpg") || filename.endsWith(".jpeg")) {
      return "image/jpeg"
    }
  }

  if (kind === "video" && filename.endsWith(".mp4")) {
    return "video/mp4"
  }

  if (kind === "audio") {
    if (filename.endsWith(".wav")) return "audio/wav"
    if (filename.endsWith(".mp3") || filename.endsWith(".mpeg")) {
      return "audio/mpeg"
    }
  }

  return limit.mimeTypes[0]
}

export const validateLocalUploadFile = (
  file: File,
  kind: UploadResourceLimitKind,
) => {
  const limit = uploadResourceLimits[kind]

  if (!isAllowedUploadFileType(file, kind)) {
    return {
      message: `${limit.label}格式仅支持 ${limit.extensions.join(" / ")}`,
      valid: false,
    }
  }

  if (file.size > limit.maxSize) {
    return {
      message: `${limit.label}大小不能超过 ${limit.sizeText}`,
      valid: false,
    }
  }

  return {
    message: "",
    valid: true,
  }
}

const getLocalImageDimensions = (file: File) => {
  return new Promise<{ height: number; width: number }>((resolve, reject) => {
    const image = new Image()
    const url = URL.createObjectURL(file)

    const cleanup = () => {
      image.removeEventListener("load", handleLoad)
      image.removeEventListener("error", handleError)
      image.removeAttribute("src")
      URL.revokeObjectURL(url)
    }

    const handleLoad = () => {
      const dimensions = {
        height: image.naturalHeight,
        width: image.naturalWidth,
      }
      cleanup()
      resolve(dimensions)
    }

    const handleError = () => {
      cleanup()
      reject(new Error("读取图片尺寸失败"))
    }

    image.addEventListener("load", handleLoad, { once: true })
    image.addEventListener("error", handleError, { once: true })
    image.src = url
  })
}

export const validateLocalImageDimensions = async (file: File) => {
  let dimensions: { height: number; width: number }

  try {
    dimensions = await getLocalImageDimensions(file)
  } catch {
    return {
      message: "无法读取图片尺寸，请重新选择图片",
      valid: false,
    }
  }

  const { height, width } = dimensions
  const {
    maxAspectRatio,
    maxHeight,
    maxWidth,
    minAspectRatio,
    minHeight,
    minWidth,
  } = imageUploadDimensionLimits

  if (
    width < minWidth ||
    width > maxWidth ||
    height < minHeight ||
    height > maxHeight
  ) {
    return {
      message: `图片宽高需在 ${minWidth}-${maxWidth}px 之间`,
      valid: false,
    }
  }

  const aspectRatio = width / height

  if (aspectRatio < minAspectRatio || aspectRatio > maxAspectRatio) {
    return {
      message: `图片宽高比需在 ${minAspectRatio}-${maxAspectRatio} 之间`,
      valid: false,
    }
  }

  return {
    message: "",
    valid: true,
  }
}

export type VideoUrlMetadata = {
  duration: number
  height: number
  width: number
}

export const getVideoUrlMetadata = (url: string, timeoutMs = 10000) => {
  return new Promise<VideoUrlMetadata>((resolve, reject) => {
    const video = document.createElement("video")
    const timer = window.setTimeout(() => {
      cleanup()
      reject(new Error("读取视频元数据超时"))
    }, timeoutMs)

    const cleanup = () => {
      window.clearTimeout(timer)
      video.removeEventListener("loadedmetadata", handleLoadedMetadata)
      video.removeEventListener("error", handleError)
      video.removeAttribute("src")
      video.load()
    }

    const handleLoadedMetadata = () => {
      const metadata = {
        duration: video.duration,
        height: video.videoHeight,
        width: video.videoWidth,
      }
      cleanup()
      resolve(metadata)
    }

    const handleError = () => {
      cleanup()
      reject(new Error("读取视频元数据失败"))
    }

    video.preload = "metadata"
    video.addEventListener("loadedmetadata", handleLoadedMetadata)
    video.addEventListener("error", handleError)
    video.src = url
  })
}

export const getVideoUrlDuration = async (url: string, timeoutMs = 10000) => {
  const metadata = await getVideoUrlMetadata(url, timeoutMs)
  return metadata.duration
}

// 本地文件没有网络开销，超时放宽，只用于兜底异常文件
export const getLocalVideoDuration = async (file: File) => {
  const url = URL.createObjectURL(file)

  try {
    return await getVideoUrlDuration(url, 60000)
  } finally {
    URL.revokeObjectURL(url)
  }
}

export const isValidUploadedVideoResource = (resource: UploadedResource) => {
  if (!isMp4Resource(resource)) return false
  return !(
    typeof resource.size === "number" && resource.size > maxVideoFileSize
  )
}
